import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, KeyRound, Save, ArrowLeft, Download, Calendar } from "lucide-react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { auth as authLib, API } from "@/lib/api";

export default function Profile() {
  const { branding } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const initialTab = params.get("tab") === "password" ? "password" : "info";

  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", dob: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [attRange, setAttRange] = useState({ from: "", to: "" });

  useEffect(() => {
    api.get("/me/profile").then((r) => {
      setMe(r.data);
      setForm({
        name: r.data.name || "",
        email: r.data.email || "",
        dob: r.data.dob || "",
      });
    }).catch(() => toast.error("Failed to load profile"));
  }, []);

  async function saveInfo() {
    setSaving(true);
    try {
      const body = { name: form.name, email: form.email, dob: form.dob };
      const r = await api.put("/me/profile", body);
      setMe(r.data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  }

  async function changePassword() {
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    if (pwd.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.put("/me/password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally { setSaving(false); }
  }

  async function downloadAttendance() {
    try {
      const params = new URLSearchParams();
      if (attRange.from) params.set("date_from", attRange.from);
      if (attRange.to) params.set("date_to", attRange.to);
      const resp = await fetch(`${API}/me/attendance/pdf?${params}`, {
        headers: { Authorization: `Bearer ${authLib.token()}` },
      });
      if (!resp.ok) throw new Error("PDF generation failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `attendance-${me?.emp_id || "report"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <div className="p-6 lg:p-10 max-w-3xl mx-auto space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: brand }}>Account</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">My Profile</h1>
            <p className="text-slate-500 mt-1 text-sm">Update your details or change your password. Changes apply immediately to your next login.</p>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)} className="rounded-xl h-9" data-testid="profile-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
        </header>

        <Tabs defaultValue={initialTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="info" data-testid="profile-tab-info"><User className="h-4 w-4 mr-1.5" />Personal Info</TabsTrigger>
            <TabsTrigger value="password" data-testid="profile-tab-password"><KeyRound className="h-4 w-4 mr-1.5" />Password</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="profile-tab-attendance"><Calendar className="h-4 w-4 mr-1.5" />Attendance</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-4">
            <Card className="border-slate-200 rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
                  </Field>
                  <Field label="Employee ID" hint="Read-only">
                    <Input value={me?.emp_id || "—"} disabled data-testid="profile-empid" />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
                  </Field>
                  <Field label="Date of Birth">
                    <Input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="profile-dob" />
                  </Field>
                </div>
                <Button onClick={saveInfo} disabled={saving} className="text-white rounded-xl" style={{ background: brand }} data-testid="profile-save-info">
                  <Save className="h-4 w-4 mr-1.5" /> Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="password" className="mt-4">
            <Card className="border-slate-200 rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <Field label="Current Password">
                  <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="profile-current-pwd" />
                </Field>
                <Field label="New Password" hint="Minimum 6 characters">
                  <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="profile-new-pwd" />
                </Field>
                <Field label="Confirm New Password">
                  <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="profile-confirm-pwd" />
                </Field>
                <Button onClick={changePassword} disabled={saving || !pwd.current_password || !pwd.new_password} className="text-white rounded-xl" style={{ background: brand }} data-testid="profile-save-pwd">
                  <KeyRound className="h-4 w-4 mr-1.5" /> Change Password
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attendance" className="mt-4">
            <Card className="border-slate-200 rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <div>
                  <div className="font-display font-black text-lg">Your Training & Attendance Report</div>
                  <p className="text-sm text-slate-500 mt-1">Pick a date range and download your PDF — handy for compliance audits & personal records.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="From">
                    <Input type="date" value={attRange.from} onChange={(e)=>setAttRange({...attRange, from: e.target.value})} data-testid="att-from"/>
                  </Field>
                  <Field label="To">
                    <Input type="date" value={attRange.to} onChange={(e)=>setAttRange({...attRange, to: e.target.value})} data-testid="att-to"/>
                  </Field>
                </div>
                <Button onClick={downloadAttendance} className="text-white rounded-xl" style={{ background: brand }} data-testid="att-download">
                  <Download className="h-4 w-4 mr-1.5"/>Download PDF
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
      {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}
